import React, { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from '../../../ui/toast';
import {
  awsUpload,
  getStorageLink,
} from '../../../utils/api_service/storage_api';
import { updateProfilePic } from '../../../utils/api_service/user_api';
import { IS_IOS } from '../../assessment/perform/assigned_assessments/assigned_assessments';

export type LiveCameraModalProps = {
  isOpen?: boolean;
  setProfileLink: (content: any) => void;
  setIsModalOpen: (content: boolean) => void;
  currentProfilePic?: string | null;
  userName?: string;
  userRole?: string;
};

type CaptureTab = 'camera' | 'upload';

export const LiveCameraModal: React.FC<LiveCameraModalProps> = ({
  isOpen = true,
  setProfileLink,
  setIsModalOpen,
  currentProfilePic,
  userName = 'Rajitha Priyankara',
  userRole,
}) => {
  const [activeTab, setActiveTab] = useState<CaptureTab>('camera');
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isFlashActive, setIsFlashActive] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Stop camera stream utility
  const stopCameraStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  // Initialize camera
  const startCamera = useCallback(async () => {
    stopCameraStream();
    setCameraError(null);

    try {
      const constraints: MediaStreamConstraints = {
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user',
        },
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        await videoRef.current.play().catch(() => {});
      }
    } catch (err: any) {
      console.warn('Camera access error:', err);
      setCameraError(
        err.name === 'NotAllowedError'
          ? 'Camera permission denied. Please allow camera access in your browser or choose Upload Photo.'
          : 'Unable to access camera. Please check your webcam connection or choose Upload Photo.',
      );
    }
  }, [stopCameraStream]);

  // Handle modal lifecycle & tab switching
  useEffect(() => {
    if (isOpen && activeTab === 'camera' && !capturedImage) {
      startCamera();
    } else {
      stopCameraStream();
    }

    return () => {
      stopCameraStream();
    };
  }, [isOpen, activeTab, capturedImage, startCamera, stopCameraStream]);

  // ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleCloseModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleCloseModal = () => {
    stopCameraStream();
    setIsModalOpen(false);
  };

  // Capture snapshot
  const handleCapturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;

    setIsCapturing(true);
    setIsFlashActive(true);
    setTimeout(() => setIsFlashActive(false), 150);

    const canvas = document.createElement('canvas');
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      // Mirror natural selfie view
      ctx.translate(width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            setCapturedBlob(blob);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
            setCapturedImage(dataUrl);
            stopCameraStream();
          } else {
            toast.error('Could not capture photo. Please try again.');
          }
          setIsCapturing(false);
        },
        'image/jpeg',
        0.92,
      );
    } else {
      setIsCapturing(false);
    }
  };

  // Retake
  const handleRetake = () => {
    setCapturedImage(null);
    setCapturedBlob(null);
    if (activeTab === 'camera') {
      startCamera();
    }
  };

  // File upload handler
  const handleFileChange = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (JPEG, PNG, or WEBP)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image file must be under 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setCapturedImage(result);
      setCapturedBlob(file);
      stopCameraStream();
    };
    reader.readAsDataURL(file);
  };

  // Drag & drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  // Save to S3 & update user profile
  const handleSavePicture = async () => {
    if (!capturedBlob) {
      toast.error('No picture selected');
      return;
    }

    setIsSaving(true);
    const fileName = `profile-${Date.now()}.jpg`;
    const folderName = 'profile-pic';

    try {
      const storageResponse = await getStorageLink(fileName, folderName);
      if (!storageResponse || !storageResponse.url || !storageResponse.fileName) {
        throw new Error('Failed to get upload link');
      }

      const uploadResult = await awsUpload(storageResponse.url, capturedBlob);
      if (uploadResult.status !== 200 && uploadResult.status !== 204) {
        throw new Error(`Upload failed with status ${uploadResult.status}`);
      }

      const updateResponse = await updateProfilePic(storageResponse.fileName);

      let updatedPicUrl = '';
      if (updateResponse?.profilePic && updateResponse.profilePic !== 'null' && updateResponse.profilePic !== 'undefined') {
        updatedPicUrl = updateResponse.profilePic;
      } else if (capturedImage) {
        updatedPicUrl = capturedImage;
      }

      if (updatedPicUrl) {
        setProfileLink(updatedPicUrl);
        localStorage.setItem('profilePic', updatedPicUrl);
        window.dispatchEvent(
          new CustomEvent('profilePicUpdated', { detail: { profilePic: updatedPicUrl } }),
        );
        window.dispatchEvent(new Event('storage'));
      }


      toast.success('Profile picture updated successfully');
      handleCloseModal();
    } catch (error: any) {
      console.error('Failed to upload profile picture:', error);
      toast.error(error?.message || 'Failed to update profile picture. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className='fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-5 bg-slate-900/50 backdrop-blur-sm transition-all duration-200'
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCloseModal();
      }}
    >
      {/* Light Theme Apple-Style Modal Card */}
      <div
        className='relative w-full max-w-2xl overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-2xl text-slate-900 flex flex-col transition-all duration-200 max-h-[92vh]'
        role='dialog'
        aria-modal='true'
      >
        {/* Shutter Flash Animation */}
        {isFlashActive && (
          <div className='absolute inset-0 z-50 bg-white opacity-80 pointer-events-none transition-opacity duration-150' />
        )}

        {/* Modal Header */}
        <div className='px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0'>
          <div>
            <h2 className='text-lg font-semibold tracking-tight text-slate-900'>
              Update Profile Picture
            </h2>
            <p className='text-xs text-slate-500 mt-0.5'>
              Take a photo with your camera or upload an image
            </p>
          </div>

          {/* Clean Apple Close Button */}
          <button
            onClick={handleCloseModal}
            className='w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors duration-150 focus:outline-none'
            aria-label='Close'
          >
            <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
            </svg>
          </button>
        </div>

        {/* Apple Segmented Pill Switcher */}
        {!capturedImage && (
          <div className='px-6 pt-4 pb-1'>
            <div className='inline-flex p-1 rounded-xl bg-slate-100 border border-slate-200/80'>
              <button
                type='button'
                onClick={() => {
                  setActiveTab('camera');
                  setCapturedImage(null);
                }}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-150 ${
                  activeTab === 'camera'
                    ? 'bg-white text-slate-900 shadow-sm border border-slate-200/60'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z'
                  />
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M15 13a3 3 0 11-6 0 3 3 0 016 0z'
                  />
                </svg>
                Camera
              </button>

              <button
                type='button'
                onClick={() => {
                  setActiveTab('upload');
                  setCapturedImage(null);
                }}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-150 ${
                  activeTab === 'upload'
                    ? 'bg-white text-slate-900 shadow-sm border border-slate-200/60'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12'
                  />
                </svg>
                Upload Photo
              </button>
            </div>
          </div>
        )}

        {/* Modal Body */}
        <div className='p-6 overflow-y-auto flex-1 flex flex-col items-center justify-center min-h-[340px]'>
          {/* CAMERA TAB */}
          {activeTab === 'camera' && !capturedImage && (
            <div className='w-full flex flex-col items-center gap-4'>
              {/* Error State */}
              {cameraError ? (
                <div className='w-full max-w-md p-6 rounded-2xl bg-amber-50 border border-amber-200 text-center flex flex-col items-center gap-3'>
                  <div className='w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700'>
                    <svg className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                      />
                    </svg>
                  </div>
                  <p className='text-xs sm:text-sm text-slate-700'>{cameraError}</p>
                  <div className='flex gap-2.5 mt-1'>
                    <button
                      onClick={startCamera}
                      className='px-3.5 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-all'
                    >
                      Retry Camera
                    </button>
                    <button
                      onClick={() => setActiveTab('upload')}
                      className='px-3.5 py-1.5 rounded-lg bg-emerald-600 text-xs font-medium text-white hover:bg-emerald-700 transition-all'
                    >
                      Upload Photo
                    </button>
                  </div>
                </div>
              ) : (
                /* Clean Camera Viewfinder */
                <div className='relative w-full max-w-[480px] aspect-[4/3] rounded-2xl overflow-hidden bg-slate-950 border border-slate-200 shadow-md'>
                  {/* Live Video */}
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline={IS_IOS}
                    muted
                    className='w-full h-full object-cover transform -scale-x-100'
                  />

                  {/* Clean Subtle Oval Face Guide */}
                  <div className='absolute inset-0 pointer-events-none flex items-center justify-center'>
                    <div className='w-[200px] sm:w-[220px] h-[260px] sm:h-[280px] rounded-[50%] border-2 border-dashed border-white/70 shadow-sm' />
                  </div>

                  {/* Bottom Guide Text */}
                  <div className='absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm text-[11px] text-white/90 font-medium pointer-events-none'>
                    Center your face inside the oval
                  </div>
                </div>
              )}

              {/* Shutter Button */}
              {!cameraError && (
                <div className='flex flex-col items-center gap-1.5 mt-1'>
                  <button
                    type='button'
                    onClick={handleCapturePhoto}
                    disabled={isCapturing}
                    className='group relative flex items-center justify-center w-16 h-16 rounded-full p-1 border-2 border-slate-300 hover:border-slate-400 transition-all duration-150 active:scale-95 shadow-sm'
                    aria-label='Take Photo'
                  >
                    <div className='w-12 h-12 rounded-full bg-emerald-600 group-hover:bg-emerald-500 transition-all shadow flex items-center justify-center'>
                      <svg className='w-5 h-5 text-white' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z'
                        />
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M15 13a3 3 0 11-6 0 3 3 0 016 0z'
                        />
                      </svg>
                    </div>
                  </button>
                  <span className='text-[11px] font-medium text-slate-500'>
                    Take Photo
                  </span>
                </div>
              )}
            </div>
          )}

          {/* UPLOAD TAB */}
          {activeTab === 'upload' && !capturedImage && (
            <div className='w-full max-w-md flex flex-col items-center gap-3 py-2'>
              <input
                ref={fileInputRef}
                type='file'
                accept='image/jpeg,image/png,image/webp'
                className='hidden'
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    handleFileChange(e.target.files[0]);
                  }
                }}
              />

              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`w-full aspect-[4/3] max-h-[280px] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-3 cursor-pointer p-6 transition-all duration-150 ${
                  dragOver
                    ? 'border-emerald-500 bg-emerald-50/50'
                    : 'border-slate-200 bg-slate-50/50 hover:bg-slate-100/60 hover:border-slate-300'
                }`}
              >
                <div className='w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600'>
                  <svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={1.8}
                      d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
                    />
                  </svg>
                </div>

                <div className='text-center'>
                  <p className='text-sm font-medium text-slate-800'>
                    Drag and drop your photo here
                  </p>
                  <p className='text-xs text-slate-500 mt-0.5'>
                    or <span className='text-emerald-600 font-medium hover:underline'>browse files</span> from your computer
                  </p>
                </div>

                <p className='text-[11px] text-slate-400'>
                  Supports JPG, PNG or WEBP (Max 10MB)
                </p>
              </div>
            </div>
          )}

          {/* CAPTURED / SELECTED PREVIEW */}
          {capturedImage && (
            <div className='w-full grid grid-cols-1 sm:grid-cols-2 gap-6 items-center'>
              {/* Captured Photo */}
              <div className='flex flex-col items-center gap-2'>
                <p className='text-xs font-semibold uppercase tracking-wider text-slate-400 self-start'>
                  Selected Photo
                </p>
                <div className='relative w-full aspect-[4/3] rounded-xl overflow-hidden border border-slate-200 bg-slate-100 shadow-sm'>
                  <img
                    src={capturedImage}
                    alt='Preview'
                    className='w-full h-full object-cover'
                  />
                </div>
              </div>

              {/* Profile Preview Card */}
              <div className='flex flex-col items-center gap-3 bg-slate-50 p-5 rounded-xl border border-slate-200/80'>
                <p className='text-xs font-semibold uppercase tracking-wider text-slate-400 self-start'>
                  Profile Preview
                </p>

                {/* Avatar Preview */}
                <div className='w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-md'>
                  <img
                    src={capturedImage}
                    alt='Avatar Preview'
                    className='w-full h-full object-cover'
                  />
                </div>

                <div className='text-center'>
                  <p className='text-sm font-semibold text-slate-900'>
                    {userName}
                  </p>
                  <p className='text-xs text-slate-500'>
                    This is how your photo will appear
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className='px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between flex-shrink-0'>
          {capturedImage ? (
            <>
              <button
                type='button'
                onClick={handleRetake}
                disabled={isSaving}
                className='flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-slate-200 text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-100 transition-all disabled:opacity-50'
              >
                <svg className='w-4 h-4 text-slate-500' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' />
                </svg>
                Retake
              </button>

              <div className='flex items-center gap-2.5'>
                <button
                  type='button'
                  onClick={handleCloseModal}
                  disabled={isSaving}
                  className='px-4 py-2 rounded-lg text-xs sm:text-sm font-medium text-slate-500 hover:text-slate-800 transition-all disabled:opacity-50'
                >
                  Cancel
                </button>

                <button
                  type='button'
                  onClick={handleSavePicture}
                  disabled={isSaving}
                  className='flex items-center gap-2 px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-medium shadow-sm transition-all active:scale-95 disabled:opacity-50 disabled:pointer-events-none'
                >
                  {isSaving ? (
                    <>
                      <svg className='animate-spin w-4 h-4 text-white' fill='none' viewBox='0 0 24 24'>
                        <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                        <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save Picture'
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className='w-full flex items-center justify-between'>
              <span className='text-xs text-slate-400'>
                {activeTab === 'camera'
                  ? 'Click the green button to take your photo'
                  : 'Select an image file from your computer'}
              </span>

              <button
                type='button'
                onClick={handleCloseModal}
                className='px-4 py-1.5 rounded-lg bg-white border border-slate-200 text-xs sm:text-sm font-medium text-slate-600 hover:bg-slate-50 transition-all'
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
